/**
 * The parent-facing "your auto-order can't be funded" alert.
 *
 * Same shape and weight as the Lifecycle alert already on the auto-order
 * detail page (`pages/family-subscriptions/detail.tsx`): a bordered muted row,
 * an amber `AlertCircle`, muted text, one small button. Deliberately quiet —
 * this sits above the week grid on the parent's landing page, not in a modal.
 *
 * The button opens the SHARED `AutoReloadDialog`, so enabling auto-reload
 * fixes the problem in place: saving invalidates `['billing','family']` AND
 * `['family-subscriptions']`, the alert's own source, so it disappears without
 * a navigation.
 */

import { Button } from '@ordo/ui/button'
import { AlertCircle } from 'lucide-react'
import { useState } from 'react'

import type { BillingFamilyAccount } from '@/api/billing-family'
import type { AutoOrderAutoReloadAlert } from '@/api/family-subscriptions'
import { autoReloadAlertCopy } from '@/lib/auto-reload-alert'

import { AutoReloadDialog } from './auto-reload-dialog'
import { AUTO_RELOAD_TRIGGER_SENTENCE } from './helpers'

export interface AutoReloadAlertProps {
  alert: AutoOrderAutoReloadAlert
  /**
   * The household the alert belongs to. Null while billing is still loading —
   * the alert still renders (the problem is real either way); the action is
   * held back until there's an account to edit.
   */
  account: BillingFamilyAccount | null
  onFixed?: () => void
}

export function AutoReloadAlert({ alert, account, onFixed }: AutoReloadAlertProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const copy = autoReloadAlertCopy(alert)

  return (
    <>
      <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-muted/40 p-3">
        <div className="flex min-w-0 items-start gap-2">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
          <div className="min-w-0">
            <p className="text-sm font-medium">{copy.title}</p>
            <p className="mt-0.5 text-sm text-muted-foreground">{copy.body}</p>
          </div>
        </div>
        {account ? (
          <Button type="button" size="sm" onClick={() => setDialogOpen(true)}>
            {copy.actionLabel}
          </Button>
        ) : null}
      </div>

      {account ? (
        <AutoReloadDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          account={account}
          description={`Auto-orders are charged to your household balance, so auto-reload keeps them funded. ${AUTO_RELOAD_TRIGGER_SENTENCE}`}
          onSaved={() => {
            setDialogOpen(false)
            onFixed?.()
          }}
        />
      ) : null}
    </>
  )
}
